// src/components/sports/MatchCard.jsx - Only render if odds exist

import React from 'react';
import BetButton from '../common/BetButton';

const MatchCard = ({ match, sport, oddsFormat, viewMode = 'grid', onAddToBetslip }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (date - now) / (1000 * 60 * 60);
    
    const isLive = ['1H', 'HT', '2H', 'ET', 'P'].includes(match.status);
    
    if (diffHours < 0 && isLive) {
      return 'LIVE';
    } else if (diffHours < 0) {
      return 'Finished';
    } else if (diffHours < 1) {
      const minutes = Math.floor(diffHours * 60);
      return `${minutes}m`;
    } else if (diffHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const isLive = ['1H', 'HT', '2H', 'ET', 'P'].includes(match.status);
  const isCompact = viewMode === 'grid';
  
  // Get odds from match object
  const odds = match.odds || { home: null, draw: null, away: null };
  const homeOdds = odds.home ? parseFloat(odds.home) : null;
  const drawOdds = odds.draw ? parseFloat(odds.draw) : null;
  const awayOdds = odds.away ? parseFloat(odds.away) : null;
  
  const hasHomeOdds = homeOdds !== null && homeOdds > 0;
  const hasDrawOdds = drawOdds !== null && drawOdds > 0;
  const hasAwayOdds = awayOdds !== null && awayOdds > 0;
  
  // Don't render if no odds available (should be filtered by backend, but just in case)
  if (!hasHomeOdds && !hasDrawOdds && !hasAwayOdds) {
    return null;
  }

  return (
    <div className={`bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors duration-200 ${
      isCompact ? 'p-3 sm:p-4' : 'p-4 sm:p-6'
    }`}>
      {/* Match Header */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          {match.league_logo && (
            <img src={match.league_logo} alt={match.league_name} className="w-4 h-4 object-contain" />
          )}
          <div className="text-xs text-gray-400 truncate">{match.league_name}</div>
        </div>
        <div className={`text-xs ${isLive ? 'text-red-400 font-semibold' : 'text-gray-400'} whitespace-nowrap`}>
          {isLive && match.elapsed > 0 ? `${match.elapsed}'` : formatDate(match.startTime || match.fixture_date)}
        </div>
      </div>
      
      {/* Live Score Display */}
      {isLive && (match.home_score !== null || match.away_score !== null) && (
        <div className="flex justify-center gap-6 mb-4 pb-2 border-b border-gray-800">
          <div className="text-center">
            <div className="text-xs text-gray-400 truncate max-w-[80px]">{match.home_team}</div>
            <div className="text-2xl font-bold text-white">{match.home_score ?? 0}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">VS</div>
            <div className="text-xl font-bold text-gray-400">-</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400 truncate max-w-[80px]">{match.away_team}</div>
            <div className="text-2xl font-bold text-white">{match.away_score ?? 0}</div>
          </div>
        </div>
      )}
      
      {/* Teams with Logos */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2 flex-1">
          {match.home_team_logo && (
            <img src={match.home_team_logo} alt={match.home_team} className="w-6 h-6 object-contain" />
          )}
          <div className={`font-medium text-white truncate ${isCompact ? 'text-sm' : 'text-base'}`}>
            {match.home_team}
          </div>
        </div>
        
        <div className={`px-2 text-gray-400 ${isCompact ? 'text-xs' : 'text-sm'}`}>VS</div>
        
        <div className="flex items-center gap-2 flex-1 justify-end">
          <div className={`font-medium text-white truncate text-right ${isCompact ? 'text-sm' : 'text-base'}`}>
            {match.away_team}
          </div>
          {match.away_team_logo && (
            <img src={match.away_team_logo} alt={match.away_team} className="w-6 h-6 object-contain" />
          )}
        </div>
      </div>
      
      {/* Betting Odds */}
      <div className="flex space-x-1 sm:space-x-2">
        {/* Home Odds */}
        <BetButton
          odds={homeOdds}
          oddsFormat={oddsFormat}
          selection="1"
          selectionName={match.home_team}
          match={`${match.home_team} vs ${match.away_team}`}
          matchId={match.id}
          sport={sport}
          type="home"
          isCompact={isCompact}
          disabled={!hasHomeOdds}
          onAddToBetslip={onAddToBetslip}
        />
        
        {/* Draw Odds */}
        <BetButton
          odds={drawOdds}
          oddsFormat={oddsFormat}
          selection="X"
          selectionName="Draw"
          match={`${match.home_team} vs ${match.away_team}`}
          matchId={match.id}
          sport={sport}
          type="draw"
          isCompact={isCompact}
          disabled={!hasDrawOdds}
          onAddToBetslip={onAddToBetslip}
        />
        
        {/* Away Odds */}
        <BetButton
          odds={awayOdds}
          oddsFormat={oddsFormat}
          selection="2"
          selectionName={match.away_team}
          match={`${match.home_team} vs ${match.away_team}`}
          matchId={match.id}
          sport={sport}
          type="away"
          isCompact={isCompact}
          disabled={!hasAwayOdds}
          onAddToBetslip={onAddToBetslip}
        />
      </div>
    </div>
  );
};

export default MatchCard;